import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useRecordSafetyAnalysisVersion } from 'components/IAP/planningView/widgets/safetyAnalysisVersionRecording';
import type { Geometry } from 'geojson';
import config from '../../../constants/apiConfig';

export type IncidentArea = {
  areaId?: UUID;
  incidentId: UUID;
  areaName: string;
  hazard: string;
  mitigations: string;
  riskGain: string | null;
  /**
   * Where the area sits, if anyone has drawn it. Raw GeoJSON, as the server
   * stores it, so the "215A Incident Areas" map layer can use it unchanged.
   */
  geometry?: Geometry | null;
};

export const keys = {
  base: 'areas',
  get all() {
    return [keys.base];
  },
  get: function (groupGuid: UUID, userGuid: UUID) {
    return [keys.base, groupGuid, userGuid];
  },
} as const;

export const useIncidentAreas = (incidentId: string) => {
  return useQuery({
    queryKey: ['areas', incidentId],
    queryFn: async () => {
      const resp = await axios.get<IncidentArea[]>(
        `${config.apiGateway.ics_incident_areas}/${incidentId}`
      );
      return resp.data;
    },
    enabled: !!incidentId,
    throwOnError: () => {
      return false;
    },
  });
};

// Areas are boxes 5-8 of the 215A, a versioned form, so every write should
// leave a draft version behind. Recorded after the write lands, so the server
// snapshots values that are already persisted.

export const useCreateIncidentArea = () => {
  const queryClient = useQueryClient();
  const recordVersion = useRecordSafetyAnalysisVersion();
  return useMutation({
    mutationFn: async (newIncidentArea: IncidentArea) => {
      const resp = await axios.post<IncidentArea>(
        `${config.apiGateway.ics_incident_areas}`,
        newIncidentArea
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      recordVersion();
    },
  });
};

export const useUpdateIncidentArea = () => {
  const queryClient = useQueryClient();
  const recordVersion = useRecordSafetyAnalysisVersion();
  return useMutation({
    mutationFn: async (updatedIncidentArea: IncidentArea) => {
      const resp = await axios.put<IncidentArea>(
        `${config.apiGateway.ics_incident_areas}/${updatedIncidentArea.areaId}`,
        updatedIncidentArea
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      recordVersion();
    },
  });
};

export const useDeleteIncidentArea = () => {
  const queryClient = useQueryClient();
  const recordVersion = useRecordSafetyAnalysisVersion();
  return useMutation({
    mutationFn: async (id: UUID) => {
      await axios.delete(`${config.apiGateway.ics_incident_areas}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      recordVersion();
    },
  });
};
